/**
 * @file Service for initiating the authentication flow
 * @author Heino Lenting
 */
import * as tslib_1 from "tslib";
import { EventEmitter } from '@angular/core';
import { SettingsService } from './settings.service';
import { OAuthService, OAuthErrorEvent } from 'angular-oauth2-oidc';
import { HttpClient, HttpParams } from '@angular/common/http';
import { JwksValidationHandler } from 'angular-oauth2-oidc-jwks';
import * as moment from 'moment';
import { AbstractBase } from './core/helpers/abstract-base';
var AuthenticationService = /** @class */ (function (_super) {
    tslib_1.__extends(AuthenticationService, _super);
    /**
    * Creates an instance of AuthenticationService.
    * @param {HttpClient} httpClient
    * @param {SettingsService} settingsService
    * @param {OAuthService} oAuthService
    * @memberof AuthenticationService
    */
    function AuthenticationService(httpClient, settingsService, oAuthService) {
        var _this = _super.call(this) || this;
        _this.httpClient = httpClient;
        _this.settingsService = settingsService;
        _this.oAuthService = oAuthService;
        _this.removeRefreshIframe = new EventEmitter();
        _this.debugMode = false;
        // Available events: https://github.com/manfredsteyer/angular-oauth2-oidc/blob/master/projects/lib/src/events.ts
        if (_this.debugMode === true) {
            _this.subscriptionPool.add(oAuthService.events.subscribe(function (event) {
                if (event instanceof OAuthErrorEvent) {
                    console.error(event);
                }
                else {
                    console.warn(event);
                }
                if (event.type === 'silent_refresh_timeout') {
                    _this.removeRefreshIframe.emit(true);
                }
            }));
        }
        return _this;
    }
    /**
    * Methode is part of Bootflow in app.module.ts
    */
    AuthenticationService.prototype.bootInit = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.initialiseOAuthService()];
                    case 1:
                        _a.sent();
                        return [4 /*yield*/, this.mustBeAuthenticated()];
                    case 2:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    AuthenticationService.prototype.initialiseOAuthService = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var oAuthServiceConfiguration, issuer, logoutUrl, customQueryParams, issuer, logoutUrl;
            return tslib_1.__generator(this, function (_a) {
                // Determine whether Auth0 is the Identity Provider.
                if (this.authenticationType() === 'auth0') {
                    this.oAuthService.tokenValidationHandler = new JwksValidationHandler();
                }
                oAuthServiceConfiguration = {
                    loginUrl: this.settingsService.settings.frontend.oauth.login_url,
                    redirectUri: this.settingsService.settings.frontend.oauth.redirectUrl,
                    silentRefreshRedirectUri: this.settingsService.settings.frontend.oauth.redirectUrl,
                    clientId: this.settingsService.settings.frontend.oauth.clientId,
                    resource: this.settingsService.settings.frontend.oauth.resource,
                    scope: this.settingsService.settings.frontend.oauth.scopes,
                    oidc: true
                };
                if (this.authenticationType() === 'auth0') {
                    issuer = !this.settingsService.settings.frontend.oauth.issuer_url.endsWith('/') ? this.settingsService.settings.frontend.oauth.issuer_url + '/' : this.settingsService.settings.frontend.oauth.issuer_url;
                    logoutUrl = this.settingsService.settings.frontend.oauth.logout_url + '?returnTo=' + this.settingsService.settings.frontend.oauth.redirectUrl + '&client_id=' + this.settingsService.settings.frontend.oauth.clientId;
                    customQueryParams = {
                        audience: this.settingsService.settings.frontend.oauth.audience
                    };
                    oAuthServiceConfiguration = tslib_1.__assign({}, oAuthServiceConfiguration, { issuer: issuer,
                        logoutUrl: logoutUrl,
                        customQueryParams: customQueryParams });
                }
                else {
                    issuer = this.settingsService.settings.frontend.oauth.issuer_url;
                    logoutUrl = this.settingsService.settings.frontend.oauth.logout_url;
                    oAuthServiceConfiguration = tslib_1.__assign({}, oAuthServiceConfiguration, { issuer: issuer,
                        logoutUrl: logoutUrl });
                }
                this.oAuthService.configure(oAuthServiceConfiguration);
                if (this.authenticationType() === 'openid' || this.authenticationType() === 'auth0') {
                    this.oAuthService.setupAutomaticSilentRefresh();
                }
                return [2 /*return*/];
            });
        });
    };
    AuthenticationService.prototype.mustBeAuthenticated = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                if (this.oAuthService.hasValidIdToken() && this.oAuthService.hasValidAccessToken()) {
                    return [2 /*return*/]; // alert('both id and access token are OK;');
                }
                return [2 /*return*/, new Promise(function (resolve, reject) {
                        _this.login().then(function () {
                            // Login failed and a redirect to authentication provicer is needed
                            if (!_this.isAuthenticated()) {
                                // Tell the user the application is redirecting for authentication (even before Angular is fully started)
                                if (window['bootProgress']) {
                                    window['bootProgress']('Redirecting for authentication', 60);
                                }
                                // After a few seconds we continue to give an error that the redirect failed
                                setTimeout(function () {
                                    console.log('delayed');
                                    reject(new Error('APP.AUTHENTICATION.REDIRECT-FAILED'));
                                }, 10 * 1000); // 10 seconds
                            }
                            else {
                                // Login is successfull and the application can continue
                                resolve();
                            }
                        }).catch(function () {
                            reject(new Error('APP.AUTHENTICATION.NOT-LOGGED-IN'));
                        });
                    })];
            });
        });
    };
    AuthenticationService.prototype.doSilentRefresh = function (forced) {
        if (forced === void 0) { forced = false; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!(forced || this.isAccessOrIdTokenExpired())) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.oAuthService.silentRefresh()];
                    case 1:
                        _a.sent();
                        return [3 /*break*/, 3];
                    case 2:
                        if (this.debugMode) {
                            console.warn('No need for a silent refresh.');
                        }
                        _a.label = 3;
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    AuthenticationService.prototype.login = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!(this.authenticationType() === 'auth0' && !this.oAuthService.jwks)) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.oAuthService.loadDiscoveryDocument()];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2: return [4 /*yield*/, this.oAuthService.tryLogin({
                            validationHandler: function (context) {
                                if (_this.authenticationType() === 'openid') {
                                    return _this.getValidationUrl(context);
                                }
                                else {
                                    return Promise.resolve();
                                }
                            }
                        })];
                    case 3:
                        _a.sent();
                        if (this.isAccessOrIdTokenExpired()) {
                            return [2 /*return*/, this.oAuthService.initImplicitFlow()];
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    AuthenticationService.prototype.logOut = function () {
        this.oAuthService.logOut();
    };
    AuthenticationService.prototype.authenticationType = function () {
        return this.settingsService.settings.frontend.oauth.authentication_type;
    };
    AuthenticationService.prototype.isAuthenticated = function () {
        return this.oAuthService.hasValidIdToken();
    };
    AuthenticationService.prototype.getValidationUrl = function (context) {
        var validationUrl = this.oAuthService.issuer + '/connect/identitytokenvalidation';
        var params = new HttpParams();
        params = params.set('token', context.idToken);
        params = params.set('client_id', this.oAuthService.clientId);
        return this.httpClient.get(validationUrl, { params: params }).toPromise();
    };
    AuthenticationService.prototype.getIdentityClaims = function () {
        return this.oAuthService.getIdentityClaims();
    };
    AuthenticationService.prototype.getAccessToken = function () {
        return this.oAuthService.getAccessToken();
    };
    AuthenticationService.prototype.getIdTokenExpiration = function () {
        return moment(this.oAuthService.getIdTokenExpiration());
    };
    AuthenticationService.prototype.getAccessTokenExpiration = function () {
        return moment(this.oAuthService.getAccessTokenExpiration());
    };
    AuthenticationService.prototype.silentRefreshIn = function () {
        var now = new Date();
        var expiration = (this.oAuthService.getIdTokenExpiration() < this.oAuthService.getAccessTokenExpiration()) ? this.oAuthService.getIdTokenExpiration() : this.oAuthService.getAccessTokenExpiration();
        return moment(expiration);
    };
    AuthenticationService.prototype.isAccessOrIdTokenExpired = function () {
        return !this.hasValidAccessToken() || !this.hasValidIdToken();
    };
    AuthenticationService.prototype.hasValidAccessToken = function () {
        return this.oAuthService.hasValidAccessToken();
    };
    AuthenticationService.prototype.hasValidIdToken = function () {
        return this.oAuthService.hasValidIdToken();
    };
    AuthenticationService.prototype.getSilentRefreshIFrameName = function () {
        return this.oAuthService.silentRefreshIFrameName;
    };
    return AuthenticationService;
}(AbstractBase));
export { AuthenticationService };
